import { useState } from "react";
import { MODULES } from "../constants/modules";
import { COLORS, FONTS } from "../constants/theme";

// ─── MODULE IMPORTS ───────────────────────────────────────────────────────────
// Each module is its own component. Import them all here.
// As each module grows in complexity, its file stays isolated.
import ExecutiveIQ from "../modules/ExecutiveIQ";
import RevenueIQ from "../modules/RevenueIQ";
import SalesIQ from "../modules/SalesIQ";
import CustomerIQ from "../modules/CustomerIQ";
import ForecastIQ from "../modules/ForecastIQ";
import MarketingIQ from "../modules/MarketingIQ";
import CompetitiveIQ from "../modules/CompetitiveIQ";
import OpsIQ from "../modules/OpsIQ";
import SupportIQ from "../modules/SupportIQ";
import PeopleIQ from "../modules/PeopleIQ";
import PartnerIQ from "../modules/PartnerIQ";
import SpendIQ from "../modules/SpendIQ";

// ─── MODULE ROUTER ────────────────────────────────────────────────────────────
// Maps module id → component. Add new modules here as they're built.
const MODULE_COMPONENTS = {
  executive: ExecutiveIQ,
  revenue: RevenueIQ,
  sales: SalesIQ,
  customer: CustomerIQ,
  forecast: ForecastIQ,
  marketing: MarketingIQ,
  competitive: CompetitiveIQ,
  ops: OpsIQ,
  support: SupportIQ,
  people: PeopleIQ,
  partner: PartnerIQ,
  spend: SpendIQ,
};

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
// The authenticated app shell. Handles:
// - Top navigation with all 12 module tabs
// - Dark / light mode toggle
// - Module routing (renders the active module component)
// - User menu + logout

export default function Dashboard({ user, onLogout }) {
  const [activeId, setActiveId] = useState("executive");
  const [visible, setVisible] = useState(true);
  const [dark, setDark] = useState(true);

  const activeMod = MODULES.find((m) => m.id === activeId);
  const ActiveComponent = MODULE_COMPONENTS[activeId];

  // Theme values — passed down to child components
  const theme = {
    dark,
    bg:       dark ? "#090d13" : "#f6f8fa",
    surface:  dark ? "#0d1117" : "#ffffff",
    surface2: dark ? "#161b22" : "#f0f4f8",
    border:   dark ? "#21262d" : "#e2e8f0",
    border2:  dark ? "#30363d" : "#cbd5e1",
    text:     dark ? "#e6edf3" : "#0f172a",
    text2:    dark ? "#c9d1d9" : "#1e293b",
    muted:    dark ? "#8b949e" : "#64748b",
    navBg:    dark ? "#090d13dd" : "#ffffffee",
  };

  const switchModule = (id) => {
    if (id === activeId) return;
    setVisible(false);
    setTimeout(() => { setActiveId(id); setVisible(true); }, 160);
  };

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: FONTS.body, transition: "background .25s" }}>
      <style>{`
        @keyframes spin-slow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-thumb { border-radius: 3px; background: #88888844; }
        .dash-mod { opacity: 0; transform: translateY(8px); transition: opacity .16s, transform .16s; }
        .dash-mod.in { opacity: 1; transform: translateY(0); }
      `}</style>

      {/* ── TOP NAV ── */}
      <nav style={{ position: "sticky", top: 0, zIndex: 20, background: theme.navBg, borderBottom: `1px solid ${theme.border}`, backdropFilter: "blur(14px)", padding: "0 20px", display: "flex", alignItems: "center", gap: 5, height: 54, overflowX: "auto", transition: "background .25s" }}>

        {/* Logo mark — color shifts to active module accent */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 12, flexShrink: 0 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: `linear-gradient(135deg,${activeMod.accent}88,${activeMod.accent})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#fff", fontFamily: FONTS.display, transition: "background .3s" }}>
            iQ
          </div>
          <span style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: 14, letterSpacing: "-0.01em", color: theme.text, whiteSpace: "nowrap" }}>
            RevCentr<span style={{ color: activeMod.accent, transition: "color .3s" }}>iQ</span>
          </span>
        </div>

        {/* Module tabs */}
        {MODULES.map((m) => (
          <button
            key={m.id}
            onClick={() => switchModule(m.id)}
            style={{
              flexShrink: 0,
              border: activeId === m.id ? `1px solid ${m.accent}44` : "1px solid transparent",
              cursor: "pointer",
              fontFamily: FONTS.body,
              fontSize: 12,
              fontWeight: 600,
              padding: "5px 12px",
              borderRadius: 20,
              transition: "all .15s",
              whiteSpace: "nowrap",
              background: activeId === m.id ? m.accent + "18" : "none",
              color: activeId === m.id ? m.accent : theme.muted,
            }}
          >
            {m.label}
          </button>
        ))}

        {/* Right controls */}
        <div style={{ marginLeft: "auto", flexShrink: 0, display: "flex", alignItems: "center", gap: 10, paddingLeft: 8 }}>
          {/* Dark / light toggle */}
          <button
            onClick={() => setDark((d) => !d)}
            style={{ display: "flex", alignItems: "center", gap: 7, background: dark ? "#21262d" : "#f1f5f9", border: `1px solid ${theme.border}`, borderRadius: 20, padding: "5px 12px 5px 6px", cursor: "pointer", transition: "all .25s" }}
          >
            <div style={{ width: 30, height: 16, borderRadius: 8, background: dark ? "#388bfd22" : "#e2e8f0", border: `1px solid ${dark ? "#388bfd44" : "#cbd5e1"}`, position: "relative" }}>
              <div style={{ position: "absolute", top: 2, left: dark ? 14 : 2, width: 10, height: 10, borderRadius: "50%", background: dark ? "#388bfd" : "#94a3b8", transition: "left .2s" }} />
            </div>
            <span style={{ fontSize: 11, fontFamily: FONTS.mono, color: theme.muted }}>{dark ? "☾" : "☀"}</span>
          </button>

          {/* User pill */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: dark ? "#161b22" : "#f1f5f9", border: `1px solid ${theme.border}`, borderRadius: 20, padding: "4px 14px 4px 6px" }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#1d4ed8,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>
              {user?.name?.split(" ").map((n) => n[0]).join("").slice(0, 2) || "U"}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: theme.text, whiteSpace: "nowrap" }}>
              {user?.name?.split(" ")[0] || "User"}
            </span>
            <button onClick={onLogout} style={{ background: "none", border: "none", color: theme.muted, cursor: "pointer", fontSize: 11, fontFamily: FONTS.mono, marginLeft: 4 }}>
              out
            </button>
          </div>
        </div>
      </nav>

      {/* ── MODULE CONTENT ── */}
      <div className={`dash-mod ${visible ? "in" : ""}`}>

        {/* ExecutiveIQ owns its own full-viewport layout — no wrapper, no page header */}
        {activeId === "executive" ? (
          <ActiveComponent
            accent={activeMod.accent}
            dark={dark}
            t={theme}
            theme={theme}
            module={activeMod}
            user={user}
          />
        ) : (
          <div style={{ padding: "28px 32px", maxWidth: 1400, margin: "0 auto" }}>

            {/* Page header */}
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: 22, letterSpacing: "-.03em", color: theme.text }}>
                {activeMod.label}
              </h1>
              <p style={{ color: theme.muted, fontSize: 12, marginTop: 3, fontFamily: FONTS.mono }}>
                {user?.company || "RevCentriQ"} · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </p>
            </div>

            {/* Active module — receives both theme (legacy) and t+dark (new) */}
            <ActiveComponent
              accent={activeMod.accent}
              dark={dark}
              t={theme}
              theme={theme}
              module={activeMod}
              user={user}
            />
          </div>
        )}

      </div>
    </div>
  );
}
